import {LAST_VIEW_CHANGED_WEB_VITALS} from '@aboutyou/logic-components';
import {Log, TadaridaWebVitals} from '@aboutyou/mlm';
import type {Metric} from 'web-vitals/attribution';

type MetricWithAttribution = Metric & {
  attribution: unknown;
};

interface Delta {
  lastRecordedTotalLayoutShift: number;
  eventParams: Record<string, unknown> | null;
}

const CLS_delta: Delta = {
  lastRecordedTotalLayoutShift: 0,
  eventParams: null,
};

const INP_delta: Delta = {
  lastRecordedTotalLayoutShift: 0,
  eventParams: null,
};

export const reportWebVitals = () => {
  import('./performance-logging').then(({webvitals}) => {
    const {onLCP, onFID, onFCP, onINP, onCLS, onTTFB} = webvitals;

    const sendToLogs =
      (metricName: TadaridaWebVitals) =>
      ({name, delta, value, id, attribution}: MetricWithAttribution) => {
        const eventParams: Record<string, unknown> = {
          metric_name: name,
          metric_id: id,
          metric_value: value,
          metric_delta: delta,
          attribution,
        };

        switch (name) {
          case 'CLS':
            eventParams.metric_score = value;
            break;
        }

        Log.reportWebVitals(metricName, eventParams);
      };

    onFCP(sendToLogs('FCP'));
    onFID(sendToLogs('FID'));
    onLCP(sendToLogs('LCP'));
    onTTFB(sendToLogs('TTFB'));
    onINP(sendToLogs('INP'), {
      reportAllChanges: false, // do not report the delta for each change but only the total for the session
    });
    onCLS(sendToLogs('CLS-session'), {
      reportAllChanges: false, // do not report the delta for each change but only the total for the session
    });

    const storeDelta =
      (stored: Delta, metricName: string) =>
      ({name, delta, value, id, attribution}: MetricWithAttribution) => {
        stored.eventParams = {
          metric_name: name,
          metric_id: id,
          metric_delta: delta,
          metric_value: value,
          metric_score: value,
          attribution,
        };
        if (process.env.NODE_ENV === 'development') {
          /* eslint-disable no-console */
          console.groupCollapsed(
            `🟡 WebVitals: ${metricName}-delta-chunk:`,
            delta,
          );
          console.info(CLS_delta.eventParams);
          console.groupEnd();
          /* eslint-enable no-console */
        }
      };

    onCLS(storeDelta(CLS_delta, 'CLS'), {
      reportAllChanges: true, // for the delta for each change
    });
    onINP(storeDelta(INP_delta, 'INP'), {
      reportAllChanges: true, // for the delta for each change
    });
  });
};

/**
 * We report the accumulated CLS/INP delta on a page (url) when the page changes.
 * Reset the accumulated CLS/INP delta when the page changes, so that next view change can be reported accurately.
 * NOTE: The sum of all (CLS/INP)-delta's reported by the pages should be equal to the reported Session (CLS/INP)
 */
const reportDelta =
  (storedDelta: Delta, metricName: 'CLS' | 'INP') => (event: Event) => {
    const {lastPageType, newPageType} = (
      event as CustomEvent<{lastPageType: string; newPageType: string}>
    ).detail;
    if (storedDelta.eventParams) {
      const totalDeltaForLastView =
        (storedDelta.eventParams.metric_score as number) -
        storedDelta.lastRecordedTotalLayoutShift;

      if (totalDeltaForLastView) {
        Log.reportWebVitals(`${metricName}-delta`, {
          ...storedDelta.eventParams,
          metric_score: totalDeltaForLastView,
          metric_value: totalDeltaForLastView,
          lastPageType,
          newPageType,
        });
      }

      storedDelta.lastRecordedTotalLayoutShift = storedDelta.eventParams
        .metric_score as number;

      storedDelta.eventParams = null;
    }
  };

// eslint-disable-next-line ssr-friendly/no-dom-globals-in-module-scope
if (process.browser) {
  window.addEventListener(
    LAST_VIEW_CHANGED_WEB_VITALS,
    reportDelta(CLS_delta, 'CLS'),
  );

  window.addEventListener(
    LAST_VIEW_CHANGED_WEB_VITALS,
    reportDelta(INP_delta, 'INP'),
  );
}
